/**
 * Noir nummerplaat-preview.
 *
 * Tekent een gesimuleerde nummerplaat (alleen visueel) bovenop de echte foto van de houder.
 * - Het ingevoerde kenteken blijft in de browser: het wordt nergens opgeslagen, verzonden of in het
 *   winkelmandje gezet (de invoervelden hebben geen name-attribuut en staan buiten het productformulier).
 * - De tekst wordt altijd als tekstknooppunt gezet (textContent), nooit als HTML.
 * - De ingevoerde waarde wordt nooit herschreven: alleen de weergave op de plaat wordt opgemaakt.
 *   Per plaattype (standaard / gepersonaliseerd / Z-plaat) wordt de invoer apart onthouden.
 * - Landformaten staan in COUNTRIES. Alleen landen met enabled:true zijn kiesbaar.
 *
 * Gebruik: NoirPlatePreview.mount(rootElement) zoekt binnen root:
 *   [data-np-layer]   <svg> of <g> waarin de plaat getekend wordt; data-np-quad = JSON met 4 hoekpunten
 *   [data-np-input], [data-np-country], [data-np-toggle]  (optioneel: bedieningselementen)
 *   [data-np-types] met knoppen [data-np-type="auto|custom|z"], [data-np-prefix], [data-np-msg]
 */
(function () {
  'use strict';
  var NS = 'http://www.w3.org/2000/svg';
  var uid = 0;
  var MAXLEN = 12;

  var COUNTRIES = {
    be: { label: 'België', enabled: true, bg: '#e9e3dc', ink: '#7a0a20', border: null, strip: '#003fcc', stripInk: '#ffffff', code: 'B', example: '1-ABC-123', zExample: 'ZAB-123', hint: 'Belgisch formaat: cijfer, 3 letters, 3 cijfers (bv. 1ABC123).' },
    nl: { label: 'Nederland', enabled: true, bg: '#f2c10e', ink: '#1a1a1a', border: null, strip: '#0a3fa6', stripInk: '#ffffff', code: 'NL', example: 'AB-123-C', hint: 'Nederlands formaat: 6 tekens, bv. AB123C.' },
    de: { label: 'Duitsland', enabled: true, bg: '#f4f4f1', ink: '#161616', border: '#161616', strip: '#0a3fa6', stripInk: '#ffffff', code: 'D', example: 'B AB 1234', seals: true, hint: 'Duits formaat: plaatscode, spatie, letters en cijfers (bv. B AB 1234). Typ de spaties zelf.' },
    fr: { label: 'Frankrijk', enabled: true, bg: '#f4f4f1', ink: '#161616', border: '#161616', strip: '#0a3fa6', stripInk: '#ffffff', code: 'F', example: 'AB-123-CD', rightBand: true, hint: 'Frans formaat: 2 letters, 3 cijfers, 2 letters (bv. AB123CD).' },
    lu: { label: 'Luxemburg', enabled: true, bg: '#f2c10e', ink: '#1a1a1a', border: null, strip: '#0a3fa6', stripInk: '#ffffff', code: 'L', example: 'AB 1234', hint: 'Luxemburgs formaat: 2 letters en 4 cijfers (bv. AB1234).' }
  };
  var Z_INK = '#3d6a34';
  var CUSTOM_EXAMPLE = 'NOIR33';

  /** Alleen A-Z, 0-9, spatie en koppelteken; hoofdletters; zonder accenten; beperkte lengte. */
  function sanitize(value, max) {
    var v = String(value == null ? '' : value);
    try { v = v.normalize('NFKD').replace(/[̀-ͯ]/g, ''); } catch (e) { /* oude browsers */ }
    v = v.replace(/<[^>]*>/g, '').toUpperCase().replace(/[^A-Z0-9 \-]/g, '');
    v = v.replace(/^[ \-]+/, '').replace(/ {2,}/g, ' ').replace(/-{2,}/g, '-');
    return v.slice(0, max || MAXLEN);
  }

  function compact(s) { return s.replace(/[ \-]/g, ''); }
  function shape(s) { return s.replace(/[A-Z]/g, 'L').replace(/[0-9]/g, 'D'); }

  // Nederlandse sidecodes: letter/cijfer-patroon -> groepering
  var NL_GROUPS = {
    LLDDDD: [2, 2, 2], DDLLDD: [2, 2, 2], DDDDLL: [2, 2, 2], LLLLDD: [2, 2, 2], DDLLLL: [2, 2, 2], LLDDLL: [2, 2, 2],
    DDLLLD: [2, 3, 1], DLLLDD: [1, 3, 2], LLDDDL: [2, 3, 1], LDDDLL: [1, 3, 2], LLLDDL: [3, 2, 1], LDDLLL: [1, 2, 3]
  };
  function groups(s, g) { var out = [], p = 0; g.forEach(function (n) { out.push(s.substr(p, n)); p += n; }); return out.join('-'); }

  /**
   * Maakt de weergavetekst. Geeft { text, ok } terug: ok = herkend standaardformaat.
   * mode: 'auto' (automatische opmaak), 'custom' (zoals getypt), 'z' (Belgische handelaarsplaat).
   * Er wordt nooit een voorvoegsel of cijfer verzonnen, behalve de Z bij de Z-plaat.
   */
  function format(country, mode, raw) {
    var typed = (raw || '').replace(/ {2,}/g, ' ');
    if (!typed) return { text: '', ok: true };
    if (mode === 'custom') return { text: typed, ok: true };
    var c = compact(typed);
    if (mode === 'z') {
      var s = c.charAt(0) === 'Z' ? c : 'Z' + c;
      var ok = /^Z[A-Z]{2}\d{3}$/.test(s);
      s = s.replace(/^(Z[A-Z]+)(\d+)$/, '$1-$2');
      return { text: s, ok: ok };
    }
    if (country === 'be') {
      if (/^\d[A-Z]{3}\d{3}$/.test(c)) return { text: c.charAt(0) + '-' + c.slice(1, 4) + '-' + c.slice(4), ok: true };
      return { text: typed, ok: false };
    }
    if (country === 'nl') {
      var g = NL_GROUPS[shape(c)];
      if (g && c.length === 6) return { text: groups(c, g), ok: true };
      return { text: typed, ok: false };
    }
    if (country === 'fr') {
      if (/^[A-Z]{2}\d{3}[A-Z]{2}$/.test(c)) return { text: c.slice(0, 2) + '-' + c.slice(2, 5) + '-' + c.slice(5), ok: true };
      return { text: typed, ok: false };
    }
    if (country === 'lu') {
      if (/^[A-Z]{2}\d{4}$/.test(c)) return { text: c.slice(0, 2) + ' ' + c.slice(2), ok: true };
      return { text: typed, ok: false };
    }
    if (country === 'de') {
      var t = typed.replace(/-/g, ' ').replace(/ {2,}/g, ' ');
      return { text: t, ok: /^[A-Z]{1,3} [A-Z]{1,2} \d{1,4}[EH]?$/.test(t) };
    }
    return { text: typed, ok: false };
  }
  // Compatibiliteit met oudere aanroepen
  function displayText(country, text) { return format(country, 'auto', text).text; }

  function el(name, attrs, parent) {
    var n = document.createElementNS(NS, name);
    for (var k in attrs) if (Object.prototype.hasOwnProperty.call(attrs, k)) n.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(n);
    return n;
  }

  function dist(a, b) { return Math.sqrt(Math.pow(a[0] - b[0], 2) + Math.pow(a[1] - b[1], 2)); }
  var FONT = 'NoirPlate, "Arial Narrow", Arial, sans-serif';

  function Plate(layer, quad) {
    var id = ++uid;
    this.id = id;
    var tl = quad[0], tr = quad[1], bl = quad[3];
    var PW = 1000, PH = Math.round(PW * dist(tl, bl) / dist(tl, tr));
    this.PW = PW; this.PH = PH;
    var a = (tr[0] - tl[0]) / PW, b = (tr[1] - tl[1]) / PW, c = (bl[0] - tl[0]) / PH, d = (bl[1] - tl[1]) / PH;
    var g = el('g', { 'class': 'np-plate', transform: 'matrix(' + [a, b, c, d, tl[0], tl[1]].map(function (n) { return +n.toFixed(5); }).join(' ') + ')', 'aria-hidden': 'true' });
    var defs = el('defs', {}, g);
    var clip = el('clipPath', { id: 'np-clip-' + id }, defs);
    el('rect', { x: 0, y: 0, width: PW, height: PH, rx: PH * 0.045 }, clip);
    var gloss = el('linearGradient', { id: 'np-gloss-' + id, x1: 0, y1: 0, x2: 0, y2: 1 }, defs);
    el('stop', { offset: '0', 'stop-color': '#ffffff', 'stop-opacity': '0.30' }, gloss);
    el('stop', { offset: '0.45', 'stop-color': '#ffffff', 'stop-opacity': '0' }, gloss);
    el('stop', { offset: '1', 'stop-color': '#000000', 'stop-opacity': '0.10' }, gloss);
    var blur = el('filter', { id: 'np-blur-' + id, x: '-5%', y: '-30%', width: '110%', height: '170%' }, defs);
    el('feGaussianBlur', { stdDeviation: PH * 0.035 }, blur);

    // zachte schaduw onder de plaat (de plaat staat voor de houder)
    el('rect', { x: PW * 0.004, y: PH * 0.035, width: PW * 0.992, height: PH * 0.97, rx: PH * 0.05, fill: '#000', opacity: '0.38', filter: 'url(#np-blur-' + id + ')' }, g);

    var body = el('g', { 'clip-path': 'url(#np-clip-' + id + ')' }, g);
    this.bg = el('rect', { x: 0, y: 0, width: PW, height: PH }, body);
    this.stripW = PW * 0.095;
    this.strip = el('rect', { x: 0, y: 0, width: this.stripW, height: PH }, body);
    this.band = el('rect', { x: PW - this.stripW, y: 0, width: this.stripW, height: PH }, body);
    this.stars = el('g', {}, body);
    this.code = el('text', { x: this.stripW / 2, y: PH * 0.8, 'text-anchor': 'middle', 'dominant-baseline': 'central', 'font-family': FONT, 'font-weight': '700', 'font-size': PH * 0.3 }, body);
    this.border = el('rect', { x: PH * 0.05, y: PH * 0.05, width: PW - PH * 0.1, height: PH - PH * 0.1, rx: PH * 0.03, fill: 'none', 'stroke-width': PH * 0.011 }, body);
    this.seals = el('g', {}, body);
    var mk = function (fill, op) { return el('text', { 'text-anchor': 'start', 'dominant-baseline': 'central', 'font-family': FONT, 'font-weight': '600', fill: fill, opacity: op || 1 }, body); };
    this.sh1 = mk('#ffffff', 0.5); this.sh2 = mk('#ffffff', 0.5);
    this.t1 = mk('#000'); this.t2 = mk('#000');
    el('rect', { x: 0, y: 0, width: PW, height: PH, fill: 'url(#np-gloss-' + id + ')' }, body);
    el('rect', { x: 0, y: 0, width: PW, height: PH, rx: PH * 0.045, fill: 'none', stroke: '#000', 'stroke-opacity': '0.4', 'stroke-width': PH * 0.012 }, g);
    // bevestigingsschroeven (op de plek van de gaten in de houder)
    var sx = PW * 0.016, sy = PH * 0.1, r = PH * 0.04;
    [[sx, sy], [PW - sx, sy], [sx, PH - sy], [PW - sx, PH - sy]].forEach(function (p) {
      el('circle', { cx: p[0], cy: p[1], r: r, fill: '#1c1918', stroke: '#6d6764', 'stroke-width': r * 0.2 }, g);
    });
    this.g = g;
    layer.appendChild(g);
    this.country = 'be';
    this.mode = 'auto';
    this.raw = '';
    this.ink = '#000';
    this.ok = true;
  }

  Plate.prototype.setStyle = function (key, mode) {
    var c = COUNTRIES[key] || COUNTRIES.be;
    this.country = key; this.mode = mode || 'auto';
    var PH = this.PH, SW = this.stripW, PW = this.PW;
    var z = this.mode === 'z' && key === 'be';
    var ink = z ? Z_INK : c.ink;
    this.ink = ink;
    this.bg.setAttribute('fill', c.bg);
    this.strip.setAttribute('fill', c.strip);
    this.band.setAttribute('fill', c.rightBand ? c.strip : 'none');
    this.code.textContent = c.code;
    this.code.setAttribute('fill', c.stripInk);
    this.code.setAttribute('font-size', PH * (c.code.length > 1 ? 0.26 : 0.3));
    this.code.setAttribute('font-weight', '700');
    this.border.setAttribute('stroke', c.border ? (z ? Z_INK : c.border) : 'none');
    this.border.setAttribute('stroke-opacity', '0.9');
    this.t1.setAttribute('fill', ink); this.t2.setAttribute('fill', ink);
    while (this.stars.firstChild) this.stars.removeChild(this.stars.firstChild);
    var cx = SW / 2, cy = PH * 0.37, R = SW * 0.3, sr = SW * 0.052;
    for (var i = 0; i < 12; i++) {
      var ang = (i / 12) * Math.PI * 2;
      el('circle', { cx: cx + R * Math.sin(ang), cy: cy - R * Math.cos(ang), r: sr, fill: '#f2c500' }, this.stars);
    }
    if (c.rightBand) { // regiozone rechts: neutrale blauwe band (de regio is niet bekend)
      var bx = PW - SW;
      el('circle', { cx: bx + SW / 2, cy: PH * 0.3, r: SW * 0.3, fill: 'none', stroke: '#ffffff', 'stroke-width': SW * 0.035, 'stroke-opacity': '0.5' }, this.stars);
    }
    this.fit();
  };

  Plate.prototype.setText = function (raw) {
    this.raw = raw;
    this.fit();
  };

  /** Zet de tekst en schaalt het lettertype (niet de vorm) zodat lange kentekens binnen de plaat blijven. */
  Plate.prototype.fit = function () {
    var c = COUNTRIES[this.country] || COUNTRIES.be;
    var z = this.mode === 'z' && this.country === 'be';
    var res = format(this.country, this.mode, this.raw);
    var example = this.mode === 'custom' ? CUSTOM_EXAMPLE : (z ? c.zExample : c.example);
    var shown = res.text || example;
    this.ok = res.ok;
    this.isExample = !this.raw;
    var PW = this.PW, PH = this.PH;
    var left = this.stripW + PW * 0.035, right = PW - (c.rightBand ? this.stripW : 0) - PW * 0.04;
    var avail = right - left, cx = (left + right) / 2, cy = PH * 0.54;
    var parts = [shown, ''];
    var gap = 0;
    if (c.seals && this.mode !== 'custom') {
      var m = /^(\S+) (.+)$/.exec(shown);
      if (m) { parts = [m[1], m[2]]; gap = PH * 0.62; }
    }
    var self = this;
    var fs = PH * 0.88;
    function apply() {
      [[self.t1, parts[0]], [self.sh1, parts[0]], [self.t2, parts[1]], [self.sh2, parts[1]]].forEach(function (p) {
        p[0].textContent = p[1]; p[0].setAttribute('font-size', fs); p[0].setAttribute('letter-spacing', fs * 0.035);
      });
    }
    function len(t) { try { return t.getComputedTextLength(); } catch (e) { return 0; } }
    apply();
    var l1 = len(this.t1), l2 = parts[1] ? len(this.t2) : 0;
    var total = l1 + (parts[1] ? gap + l2 : 0);
    if (total > avail && total > 0) { fs = fs * avail / total; apply(); l1 = len(this.t1); l2 = parts[1] ? len(this.t2) : 0; total = l1 + (parts[1] ? gap + l2 : 0); }
    var x0 = cx - total / 2;
    var off = PH * 0.008, offy = PH * 0.012;
    this.t1.setAttribute('x', x0); this.t1.setAttribute('y', cy);
    this.sh1.setAttribute('x', x0 + off); this.sh1.setAttribute('y', cy + offy);
    var x2 = x0 + l1 + gap;
    this.t2.setAttribute('x', x2); this.t2.setAttribute('y', cy);
    this.sh2.setAttribute('x', x2 + off); this.sh2.setAttribute('y', cy + offy);
    // zegelruimte (Duitsland): twee lege ringen tussen plaatscode en letters
    while (this.seals.firstChild) this.seals.removeChild(this.seals.firstChild);
    if (parts[1] && gap) {
      var sx = x0 + l1 + gap / 2, r = PH * 0.17;
      [cy - PH * 0.2, cy + PH * 0.2].forEach(function (yy) {
        el('circle', { cx: sx, cy: yy, r: r, fill: '#e6e6e1', stroke: '#b4b4ae', 'stroke-width': r * 0.12 }, self.seals);
      });
    }
  };

  Plate.prototype.setVisible = function (on) {
    this.g.style.display = on ? '' : 'none';
    if (on) this.fit();
  };

  function mount(root) {
    var layer = root.querySelector('[data-np-layer]');
    if (!layer || layer.dataset.npInit) return null;
    var quad;
    try { quad = JSON.parse(layer.getAttribute('data-np-quad')); } catch (e) { return null; }
    if (!quad || quad.length !== 4) return null;
    layer.dataset.npInit = '1';
    var plate = new Plate(layer, quad);
    var input = root.querySelector('[data-np-input]');
    var select = root.querySelector('[data-np-country]');
    var toggle = root.querySelector('[data-np-toggle]');
    var fields = root.querySelector('[data-np-fields]');
    var note = root.querySelector('[data-np-example-note]');
    var typeBox = root.querySelector('[data-np-types]');
    var typeBtns = typeBox ? [].slice.call(typeBox.querySelectorAll('[data-np-type]')) : [];
    var prefix = root.querySelector('[data-np-prefix]');
    var msg = root.querySelector('[data-np-msg]');
    // per plaattype wordt de invoer apart bewaard, zodat wisselen niets kwijtmaakt
    var state = { country: 'be', visible: true, mode: 'auto', vals: { auto: '', custom: '', z: '' } };

    function refresh() {
      var c = COUNTRIES[state.country];
      if (state.mode === 'z' && state.country !== 'be') state.mode = 'auto';
      var z = state.mode === 'z';
      plate.setStyle(state.country, state.mode);
      plate.setText(state.vals[state.mode]);
      plate.setVisible(state.visible);
      if (input) {
        if (input.value !== state.vals[state.mode]) input.value = state.vals[state.mode];
        input.placeholder = state.mode === 'custom' ? CUSTOM_EXAMPLE : (z ? 'AB123' : c.example.replace(/[- ]/g, ''));
        input.classList.toggle('np-input--z', z);
      }
      if (prefix) prefix.hidden = !z;
      typeBtns.forEach(function (b) {
        var t = b.getAttribute('data-np-type');
        if (t === 'z') b.hidden = state.country !== 'be';
        var on = t === state.mode;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.classList.toggle('is-on', on);
        b.tabIndex = on ? 0 : -1;
      });
      if (fields) fields.classList.toggle('is-off', !state.visible);
      if (note) note.hidden = !(state.visible && plate.isExample);
      if (msg) {
        var t;
        if (!state.vals[state.mode]) t = state.mode === 'custom' ? 'Gepersonaliseerd: je kenteken verschijnt precies zoals je het typt.' : (z ? 'Handelaarsplaat: de Z komt automatisch vooraan (bv. AB123 wordt ZAB-123).' : c.hint);
        else if (state.mode === 'auto' && !plate.ok) t = 'Dit past niet in het standaardformaat van ' + c.label + '; we tonen het precies zoals je het typt.';
        else if (z && !plate.ok) t = 'Een handelaarsplaat heeft de vorm Z + 2 letters + 3 cijfers; we tonen het zoals je het typt.';
        else t = '';
        msg.textContent = t;
      }
      layer.classList.toggle('is-on', state.visible);
    }
    if (select) {
      Object.keys(COUNTRIES).forEach(function (k) {
        if (!COUNTRIES[k].enabled) return;
        var o = document.createElement('option');
        o.value = k; o.textContent = COUNTRIES[k].label;
        select.appendChild(o);
      });
      select.value = state.country;
      select.addEventListener('change', function () {
        if (!COUNTRIES[select.value] || !COUNTRIES[select.value].enabled) { select.value = state.country; return; }
        state.country = select.value;
        refresh();
      });
    }
    if (input) {
      input.setAttribute('maxlength', '16'); // ruimte voor plakken; echte beperking gebeurt in sanitize()
      input.addEventListener('input', function () {
        var pos = input.selectionStart;
        var before = input.value;
        var v = sanitize(before);
        if (v !== before) {
          input.value = v;
          try { var np = Math.max(0, Math.min(v.length, pos - (before.length - v.length))); input.setSelectionRange(np, np); } catch (e) { /* ignore */ }
        }
        state.vals[state.mode] = v;
        refresh();
      });
    }
    function setMode(m) { if (m === 'z' && state.country !== 'be') return; state.mode = m; refresh(); if (input) { try { input.focus({ preventScroll: true }); } catch (e) { /* ignore */ } } }
    typeBtns.forEach(function (b, i) {
      b.addEventListener('click', function () { setMode(b.getAttribute('data-np-type')); });
      b.addEventListener('keydown', function (e) {
        var vis = typeBtns.filter(function (x) { return !x.hidden; });
        var k = vis.indexOf(b), n = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = vis[(k + 1) % vis.length];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = vis[(k - 1 + vis.length) % vis.length];
        if (n) { e.preventDefault(); n.focus(); setMode(n.getAttribute('data-np-type')); }
      });
    });
    if (toggle) {
      state.visible = toggle.checked;
      toggle.addEventListener('change', function () { state.visible = toggle.checked; refresh(); });
    }
    refresh();
    // lettertype geladen? opnieuw passen (meting hangt van het lettertype af)
    if (document.fonts && document.fonts.load) {
      document.fonts.load('600 100px NoirPlate').then(function () { plate.fit(); }).catch(function () { /* fallback-lettertype blijft */ });
    }
    return { plate: plate, state: state, refit: function () { plate.fit(); } };
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-np-root]').forEach(function (root) {
      if (!root.noirPlate) root.noirPlate = mount(root);
    });
  }

  window.NoirPlatePreview = { mount: mount, init: init, sanitize: sanitize, countries: COUNTRIES, displayText: displayText, format: format };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
